/** Turn a project into a WIG — or change one — in six short steps.
 *
 * Nothing is recreated (Brian, 2026-09-29). The project keeps its dates, work, milestones,
 * trackers and team; the wizard adds the goal, the measures and the weekly cadence on top,
 * pre-filled from what the project already knows:
 *
 *   1. Check    — is it live, does it have a date, is it already a WIG?
 *   2. Result   — the lag measure: a tracker or the milestones if the project counts itself,
 *                 or a number the lane will type in.
 *   3. Goal     — "From X to Y by WHEN", suggested from the result; WHEN is the target date.
 *   4. Weekly   — the lead measures. Nothing can detect these — they are what the team
 *                 *does* — so the wizard offers the arithmetic instead: the weekly pace the
 *                 result needs.
 *   5. People   — one owner (who may own no other WIG) and any supporters; the session day.
 *   6. Review   — the lane as it will look, before anything is saved.
 */

import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Plus, Trash2, X, XCircle } from 'lucide-react'

import { Button, DateInput, ErrorState, Field, Loading, inputClass } from '@/components/ui'
import { formatDate } from '@/lib/domain'
import {
  formatNumber,
  useCreateWig,
  useUpdateWig,
  useWigDraft,
  type MeasureInput,
  type WigDraft,
  type WigLane,
} from '@/features/wig/useWig'

const STEPS = ['Check', 'Result', 'Goal', 'Weekly', 'People', 'Review'] as const
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
const MAX_PER_KIND = 3

type Draft = MeasureInput & { key: string }

let keySeed = 0
const newKey = () => `m${++keySeed}`

function fromExisting(lane: WigLane): Draft[] {
  return [
    ...lane.lag.map(
      (m): Draft => ({
        key: newKey(),
        id: m.id,
        kind: 'lag',
        name: m.name,
        unit: m.unit,
        source: m.source,
        tracker_item_id: m.trackerItemId,
        baseline: m.baseline,
        target: m.targetFollowsSource ? null : m.target,
        weekly_target: null,
      }),
    ),
    ...lane.lead.map(
      (m): Draft => ({
        key: newKey(),
        id: m.id,
        kind: 'lead',
        name: m.name,
        unit: m.unit,
        source: 'manual',
        tracker_item_id: null,
        baseline: null,
        target: null,
        weekly_target: m.weeklyTarget,
      }),
    ),
  ]
}

function numberOrNull(value: string): number | null {
  if (value.trim() === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

/** What a lag measure currently reads and where it has to get to, for the pace helper. */
function lagNumbers(measure: Draft, draft: WigDraft): { current: number | null; target: number | null } {
  if (measure.source === 'manual') return { current: measure.baseline, target: measure.target }
  const option = draft.lagOptions.find(
    (o) => o.source === measure.source && (o.trackerItemId ?? null) === (measure.tracker_item_id ?? null),
  )
  return { current: option?.current ?? null, target: measure.target ?? option?.target ?? null }
}

function StepCheck({ draft, editing }: { draft: WigDraft; editing: boolean }) {
  if (editing) {
    return (
      <p className="text-sm text-fg-muted">
        Editing the WIG on <b className="text-fg">{draft.project.name}</b>. Measures you keep
        keep their weekly history; a removed measure is retired with its numbers, not erased.
      </p>
    )
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">
        <b className="text-fg">{draft.project.name}</b> ({draft.project.teamShortLabel}) stays
        exactly as it is. The WIG is added on top of it.
      </p>
      <ul className="space-y-2">
        {draft.checks.map((check) => {
          const Icon = check.ok ? CheckCircle2 : check.blocking ? XCircle : AlertTriangle
          const color = check.ok ? 'var(--status-green)' : check.blocking ? 'var(--status-red)' : 'var(--status-amber)'
          return (
            <li key={check.text} className="flex items-start gap-2 text-sm text-fg">
              <Icon size={16} className="mt-0.5 shrink-0" style={{ color }} aria-hidden />
              {check.text}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function MeasureRow({
  measure,
  onChange,
  onRemove,
  draft,
}: {
  measure: Draft
  onChange: (next: Draft) => void
  onRemove: () => void
  draft: WigDraft
}) {
  const set = (patch: Partial<Draft>) => onChange({ ...measure, ...patch })
  const option =
    measure.kind === 'lag' && measure.source !== 'manual'
      ? draft.lagOptions.find(
          (o) => o.source === measure.source && (o.trackerItemId ?? null) === (measure.tracker_item_id ?? null),
        )
      : undefined
  return (
    <li className="card space-y-3 p-3">
      <div className="flex items-start gap-2">
        <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_140px]">
          <Field label="Name">
            <input className={inputClass} value={measure.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Unit">
            <input
              className={inputClass}
              value={measure.unit}
              placeholder={measure.kind === 'lag' ? 'devices, sites…' : 'packs, calls…'}
              onChange={(e) => set({ unit: e.target.value })}
            />
          </Field>
        </div>
        <button type="button" onClick={onRemove} className="mt-6 text-fg-muted hover:text-fg" aria-label={`Remove ${measure.name || 'measure'}`}>
          <Trash2 size={15} aria-hidden />
        </button>
      </div>
      {measure.kind === 'lead' ? (
        <Field label="Weekly target" hint="The number the team commits to every week.">
          <input
            className={`${inputClass} !w-32`}
            inputMode="decimal"
            value={measure.weekly_target ?? ''}
            onChange={(e) => set({ weekly_target: numberOrNull(e.target.value) })}
          />
        </Field>
      ) : measure.source === 'manual' ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Where it stands today">
            <input
              className={inputClass}
              inputMode="decimal"
              value={measure.baseline ?? ''}
              onChange={(e) => set({ baseline: numberOrNull(e.target.value) })}
            />
          </Field>
          <Field label="Where it has to be by the date" hint="Lower than today counts down — lines still billing to 0.">
            <input
              className={inputClass}
              inputMode="decimal"
              value={measure.target ?? ''}
              onChange={(e) => set({ target: numberOrNull(e.target.value) })}
            />
          </Field>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <p className="text-fg-muted">{option?.detail ?? 'Reads itself.'}</p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={measure.target === null}
              onChange={(e) => set({ target: e.target.checked ? null : (option?.target ?? null) })}
            />
            <span className="text-fg">
              Finish line follows the source&apos;s own total
              {option?.target !== null && option?.target !== undefined ? ` (${formatNumber(option.target)} today)` : ''}
            </span>
          </label>
          {measure.target !== null ? (
            <Field label="Fixed finish line">
              <input
                className={`${inputClass} !w-32`}
                inputMode="decimal"
                value={measure.target ?? ''}
                onChange={(e) => set({ target: numberOrNull(e.target.value) })}
              />
            </Field>
          ) : null}
        </div>
      )}
    </li>
  )
}

export function WigWizard({
  projectId,
  existing,
  onClose,
}: {
  projectId: string
  existing?: WigLane
  onClose: () => void
}) {
  const editing = Boolean(existing)
  const { data: draft, isPending, error } = useWigDraft(projectId, true)
  const create = useCreateWig()
  const update = useUpdateWig()

  const [step, setStep] = useState(editing ? 1 : 0)
  const [measures, setMeasures] = useState<Draft[]>(() => (existing ? fromExisting(existing) : []))
  const [fromText, setFromText] = useState(existing?.fromText ?? '')
  const [toText, setToText] = useState(existing?.toText ?? '')
  const [targetDate, setTargetDate] = useState('')
  const [ownerId, setOwnerId] = useState(existing?.owner.userId ?? '')
  const [supporterIds, setSupporterIds] = useState<string[]>(existing?.supporters.map((s) => s.userId) ?? [])
  const [sessionDay, setSessionDay] = useState(existing?.sessionDay ?? 0)
  const [goalTouched, setGoalTouched] = useState(editing)
  const [peopleFilter, setPeopleFilter] = useState('')

  // Pre-fill once the draft arrives: owner and supporters from the project, and the first
  // thing the project already counts as the result measure.
  useEffect(() => {
    if (!draft || editing) return
    setOwnerId((current) => current || draft.suggestedOwnerId || '')
    setSupporterIds((current) => (current.length ? current : draft.suggestedSupporterIds))
    setMeasures((current) => {
      const first = draft.lagOptions[0]
      if (current.length || !first) return current
      return [
        {
          key: newKey(),
          kind: 'lag',
          name: first.name,
          unit: first.unit,
          source: first.source,
          tracker_item_id: first.trackerItemId,
          baseline: null,
          target: null,
          weekly_target: null,
        },
      ]
    })
  }, [draft, editing])

  // Escape closes the wizard — and only the wizard, not the drawer underneath.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [onClose])

  const lags = measures.filter((m) => m.kind === 'lag')
  const leads = measures.filter((m) => m.kind === 'lead')
  const goalDate = draft?.project.targetDate ?? (targetDate || null)

  // Suggest the goal sentence from the first result measure until somebody edits it.
  useEffect(() => {
    const first = lags[0]
    if (goalTouched || !draft || !first) return
    const { current, target } = lagNumbers(first, draft)
    const noun = first.unit || first.name.toLowerCase()
    if (current !== null) setFromText(`${formatNumber(current)} ${noun}`)
    if (target !== null) setToText(`${formatNumber(target)} ${noun}`)
  }, [draft, lags, goalTouched])

  const paceHints = useMemo(() => {
    if (!draft || !goalDate) return []
    const weeks = Math.max(
      1,
      Math.ceil((new Date(`${goalDate}T00:00:00`).getTime() - new Date(`${draft.today}T00:00:00`).getTime()) / (7 * 86_400_000)),
    )
    return lags.flatMap((lag) => {
      const { current, target } = lagNumbers(lag, draft)
      if (current === null || target === null) return []
      const perWeek = Math.abs(target - current) / weeks
      return [
        `${lag.name}: ${formatNumber(current)} → ${formatNumber(target)} in ${weeks} weeks is ${formatNumber(perWeek)} ${lag.unit || ''} a week.`,
      ]
    })
  }, [draft, goalDate, lags])

  const blocked = !editing && Boolean(draft?.checks.some((c) => c.blocking))
  const stepProblem = (() => {
    if (step === 0 && blocked) return 'Something above stops this project becoming a WIG.'
    if (step === 1) {
      if (lags.length === 0) return 'Add at least one result measure.'
      if (lags.some((m) => !m.name.trim())) return 'Every measure needs a name.'
      if (lags.some((m) => m.source === 'manual' && (m.baseline === null || m.target === null)))
        return 'A typed result needs where it stands today and where it has to be.'
    }
    if (step === 2) {
      if (!fromText.trim() || !toText.trim()) return 'Say where it starts and where it has to be.'
      if (!goalDate) return 'The goal needs a date.'
    }
    if (step === 3) {
      if (leads.length === 0) return 'Add at least one lead measure — without one the WIG cannot set health.'
      if (leads.some((m) => !m.name.trim() || !m.weekly_target || m.weekly_target <= 0))
        return 'Every lead measure needs a name and a weekly target above zero.'
    }
    if (step === 4 && !ownerId) return 'Choose the owner.'
    return null
  })()

  const save = () => {
    const payloadMeasures = measures.map(({ key: _key, ...rest }) => rest)
    const body = {
      owner_user_id: ownerId,
      supporter_ids: supporterIds.filter((id) => id !== ownerId),
      from_text: fromText.trim(),
      to_text: toText.trim(),
      session_day: sessionDay,
      measures: payloadMeasures,
    }
    if (existing) {
      update.mutate({ id: existing.id, ...body, row_version: existing.rowVersion }, { onSuccess: onClose })
    } else {
      create.mutate(
        {
          project_id: projectId,
          ...body,
          ...(draft?.project.targetDate ? {} : { target_date: targetDate }),
        },
        { onSuccess: onClose },
      )
    }
  }
  const saving = create.isPending || update.isPending
  const saveError = create.error ?? update.error

  const addLag = (option?: WigDraft['lagOptions'][number]) =>
    setMeasures((current) => [
      ...current,
      option
        ? {
            key: newKey(),
            kind: 'lag',
            name: option.name,
            unit: option.unit,
            source: option.source,
            tracker_item_id: option.trackerItemId,
            baseline: null,
            target: null,
            weekly_target: null,
          }
        : {
            key: newKey(),
            kind: 'lag',
            name: '',
            unit: '',
            source: 'manual',
            tracker_item_id: null,
            baseline: null,
            target: null,
            weekly_target: null,
          },
    ])
  const addLead = () =>
    setMeasures((current) => [
      ...current,
      { key: newKey(), kind: 'lead', name: '', unit: '', source: 'manual', tracker_item_id: null, baseline: null, target: null, weekly_target: null },
    ])
  const change = (next: Draft) => setMeasures((current) => current.map((m) => (m.key === next.key ? next : m)))
  const remove = (key: string) => setMeasures((current) => current.filter((m) => m.key !== key))

  const unusedOptions =
    draft?.lagOptions.filter(
      (o) => !lags.some((m) => m.source === o.source && (m.tracker_item_id ?? null) === (o.trackerItemId ?? null)),
    ) ?? []

  const people = (draft?.people ?? []).filter((p) =>
    `${p.displayName} ${p.email}`.toLowerCase().includes(peopleFilter.trim().toLowerCase()),
  )
  const ownerName = draft?.people.find((p) => p.userId === ownerId)?.displayName ?? '—'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-lg bg-canvas shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={editing ? 'Edit WIG' : 'Make this a WIG'}
      >
        <div className="flex items-center gap-3 border-b border-border px-6 py-4">
          <h2 className="flex-1 font-heading text-2xl text-fg">
            {editing ? 'Edit the WIG' : 'Make this a WIG'}
            {draft ? <span className="block text-sm font-normal text-fg-muted">{draft.project.name}</span> : null}
          </h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-fg-muted hover:bg-surface" aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>

        <ol className="flex gap-1 border-b border-border px-6 py-3" aria-label="Steps">
          {STEPS.map((label, index) =>
            editing && index === 0 ? null : (
              <li key={label} className="flex-1">
                <span
                  className="block rounded px-2 py-1 text-center text-xs font-semibold"
                  aria-current={index === step ? 'step' : undefined}
                  style={
                    index === step
                      ? { backgroundColor: 'var(--brand)', color: 'var(--brand-on)' }
                      : { color: index < step ? 'var(--fg)' : 'var(--fg-muted)' }
                  }
                >
                  {index + (editing ? 0 : 1)}. {label}
                </span>
              </li>
            ),
          )}
        </ol>

        <div className="flex-1 overflow-auto px-6 py-5">
          {isPending ? (
            <Loading label="Reading the project" />
          ) : !draft ? (
            // Only when there is nothing to show. A failed background refresh must not
            // hide a draft already on screen, and with it everything typed so far.
            <ErrorState error={error} />
          ) : step === 0 ? (
            <StepCheck draft={draft} editing={editing} />
          ) : step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-fg-muted">
                The <b className="text-fg">lag measure</b> is the result — what has to be true by
                the date. Where the project already counts something, use it: the scoreboard
                then maintains itself.
              </p>
              <ul className="space-y-3">
                {lags.map((m) => (
                  <MeasureRow key={m.key} measure={m} draft={draft} onChange={change} onRemove={() => remove(m.key)} />
                ))}
              </ul>
              {lags.length < MAX_PER_KIND ? (
                <div className="flex flex-wrap gap-2">
                  {unusedOptions.map((option) => (
                    <Button key={`${option.source}-${option.trackerItemId}`} onClick={() => addLag(option)}>
                      <Plus size={14} aria-hidden />
                      {option.name}
                    </Button>
                  ))}
                  <Button onClick={() => addLag()}>
                    <Plus size={14} aria-hidden />A number the lane types in
                  </Button>
                </div>
              ) : null}
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <p className="text-sm text-fg-muted">
                One sentence, in the book&apos;s form: <b className="text-fg">From X to Y by WHEN</b>.
                Suggested from the result measure — reword it the way the team would say it.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="From">
                  <input className={inputClass} value={fromText} onChange={(e) => { setGoalTouched(true); setFromText(e.target.value) }} />
                </Field>
                <Field label="To">
                  <input className={inputClass} value={toText} onChange={(e) => { setGoalTouched(true); setToText(e.target.value) }} />
                </Field>
              </div>
              {draft.project.targetDate ? (
                <p className="text-sm text-fg">
                  By <b>{formatDate(draft.project.targetDate)}</b>
                  <span className="block text-xs text-fg-muted">
                    The project&apos;s target date. To move it, change it in the project&apos;s
                    schedule, where the reason is recorded.
                  </span>
                </p>
              ) : (
                <Field label="By" hint="The project has no target date — this sets it.">
                  <DateInput value={targetDate} onChange={setTargetDate} />
                </Field>
              )}
              <div className="card p-3">
                <p className="micro-label text-fg-muted">Reads as</p>
                <p className="mt-1 font-heading text-lg text-fg">
                  From <b>{fromText || '…'}</b> to <b>{toText || '…'}</b> by{' '}
                  <b>{goalDate ? formatDate(goalDate) : '…'}</b>.
                </p>
              </div>
            </div>
          ) : step === 3 ? (
            <div className="space-y-4">
              <p className="text-sm text-fg-muted">
                <b className="text-fg">Lead measures</b> are what the team does each week that
                predicts the result — and that it controls. Nothing can detect these; the
                arithmetic below is what the result needs.
              </p>
              {paceHints.length ? (
                <ul className="card space-y-1 p-3 text-sm text-fg">
                  {paceHints.map((hint) => (
                    <li key={hint}>{hint}</li>
                  ))}
                </ul>
              ) : null}
              <ul className="space-y-3">
                {leads.map((m) => (
                  <MeasureRow key={m.key} measure={m} draft={draft} onChange={change} onRemove={() => remove(m.key)} />
                ))}
              </ul>
              {leads.length < MAX_PER_KIND ? (
                <Button onClick={addLead}>
                  <Plus size={14} aria-hidden />
                  Add a lead measure
                </Button>
              ) : null}
            </div>
          ) : step === 4 ? (
            <div className="space-y-5">
              <Field label="Owner" hint="Accountable for the weekly number by name. One WIG per owner.">
                <select className={inputClass} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                  <option value="">Choose…</option>
                  {draft.people.map((p) => {
                    const takenElsewhere = Boolean(p.ownsWig) && p.userId !== existing?.owner.userId
                    return (
                      <option key={p.userId} value={p.userId} disabled={takenElsewhere}>
                        {p.displayName}
                        {takenElsewhere ? ` — owns ${p.ownsWig}` : ''}
                      </option>
                    )
                  })}
                </select>
              </Field>
              <div>
                <p className="micro-label text-fg-muted">Supporters</p>
                <p className="mb-2 text-xs text-fg-muted">
                  They record numbers and make commitments on this lane. Supporting several WIGs is fine.
                </p>
                <input
                  className={`${inputClass} mb-2`}
                  placeholder="Filter people"
                  value={peopleFilter}
                  onChange={(e) => setPeopleFilter(e.target.value)}
                />
                <ul className="card max-h-48 divide-y divide-border overflow-auto">
                  {people
                    .filter((p) => p.userId !== ownerId)
                    .map((p) => (
                      <li key={p.userId}>
                        <label className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={supporterIds.includes(p.userId)}
                            onChange={(e) =>
                              setSupporterIds((current) =>
                                e.target.checked ? [...current, p.userId] : current.filter((id) => id !== p.userId),
                              )
                            }
                          />
                          <span className="text-fg">{p.displayName}</span>
                          <span className="text-xs text-fg-muted">{p.email}</span>
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
              <Field label="Weekly session" hint="The lane's own twenty minutes. The board review for every lane is Friday.">
                <select className={`${inputClass} !w-48`} value={sessionDay} onChange={(e) => setSessionDay(Number(e.target.value))}>
                  {DAYS.map((day, index) => (
                    <option key={day} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="card p-4">
                <p className="micro-label text-fg-muted">Wildly important goal</p>
                <p className="mt-1 font-heading text-lg text-fg">
                  From <b>{fromText}</b> to <b>{toText}</b> by <b>{goalDate ? formatDate(goalDate) : '…'}</b>.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="card p-4">
                  <p className="micro-label mb-2 text-fg-muted">Lag — the result</p>
                  <ul className="space-y-1 text-fg">
                    {lags.map((m) => (
                      <li key={m.key}>
                        {m.name}
                        <span className="text-fg-muted">
                          {m.source === 'manual'
                            ? ` · ${formatNumber(m.baseline)} → ${formatNumber(m.target)} ${m.unit}`
                            : ` · reads its ${m.source === 'tracker' ? 'tracker' : 'milestones'}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="card p-4">
                  <p className="micro-label mb-2 text-fg-muted">Lead — weekly</p>
                  <ul className="space-y-1 text-fg">
                    {leads.map((m) => (
                      <li key={m.key}>
                        {m.name}
                        <span className="text-fg-muted"> · {formatNumber(m.weekly_target)} a week</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="card p-4 text-fg">
                <b>{ownerName}</b> owns it
                {supporterIds.filter((id) => id !== ownerId).length
                  ? `, supported by ${supporterIds
                      .filter((id) => id !== ownerId)
                      .map((id) => draft.people.find((p) => p.userId === id)?.displayName ?? '')
                      .join(', ')}`
                  : ''}
                . Weekly session on {DAYS[sessionDay]}.
              </div>
              <p className="text-xs text-fg-muted">
                Once saved, this project&apos;s health is set by the WIG&apos;s pace — green at or
                ahead, amber up to 15% of the goal behind or a lead measure short two weeks
                running, red beyond 15% — and can no longer be set by hand.
              </p>
              {saveError ? <ErrorState error={saveError} /> : null}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-6 py-4">
          <Button onClick={() => (step > (editing ? 1 : 0) ? setStep(step - 1) : onClose())}>
            {step > (editing ? 1 : 0) ? 'Back' : 'Cancel'}
          </Button>
          <span className="flex-1 text-right text-xs" style={{ color: 'var(--status-amber)' }}>
            {stepProblem ?? ''}
          </span>
          {step < STEPS.length - 1 ? (
            <Button variant="primary" disabled={Boolean(stepProblem) || isPending} onClick={() => setStep(step + 1)}>
              Next: {STEPS[step + 1]}
            </Button>
          ) : (
            <Button variant="primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Make it a WIG'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
